import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import {
  Package,
  TrendingUp,
  AlertTriangle,
  FolderOpen,
  DollarSign,
  Activity,
  Users,
  ShoppingCart,
  AlertCircle,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../App';
import axios from 'axios';

import { BACKEND_URL, API } from '../config/apiConfig';

const Dashboard = () => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inventoryAlerts, setInventoryAlerts] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [dashboardResponse, alertsResponse] = await Promise.all([
        axios.get(`${API}/dashboard`),
        axios.get(`${API}/inventory/alerts`)
      ]);
      setMetrics(dashboardResponse.data);
      setInventoryAlerts(alertsResponse.data);
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-full"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Dashboard</h2>
        <p className="text-gray-600 mb-4">{error}</p>
        <Button onClick={fetchDashboardData} data-testid="retry-dashboard">
          Try Again
        </Button>
      </div>
    );
  }

  const inventoryData = Object.entries(metrics?.inventory_by_type || {}).map(([key, value]) => ({
    name: key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
    value: value,
    percentage: ((value / metrics.total_inventory_value) * 100).toFixed(1)
  }));

  const transactionData = metrics?.recent_transactions?.slice(0, 5).map((txn, index) => ({
    name: `Transaction ${index + 1}`,
    value: txn.total_value,
    type: txn.transaction_type
  })) || [];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">Welcome back, {user?.full_name}</p>
        </div>
        <Button 
          onClick={fetchDashboardData}
          variant="outline"
          data-testid="refresh-dashboard"
        >
          <Activity className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Inventory Value</CardTitle>
            <DollarSign className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900" data-testid="total-inventory-value">
              {formatCurrency(metrics?.total_inventory_value || 0)}
            </div>
            <p className="text-xs text-gray-500 mt-1">Across all items</p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Items</CardTitle>
            <Package className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900" data-testid="total-items">
              {metrics?.total_items || 0}
            </div>
            <p className="text-xs text-gray-500 mt-1">Items in inventory</p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Inventory Alerts</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900" data-testid="low-stock-alerts">
              {inventoryAlerts?.total_alerts || metrics?.low_stock_alerts || 0}
            </div>
            {inventoryAlerts && (
              <div className="flex gap-2 mt-1">
                {inventoryAlerts.critical_count > 0 && (
                  <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">{inventoryAlerts.critical_count} critical</span>
                )}
                {inventoryAlerts.high_count > 0 && (
                  <span className="text-xs px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">{inventoryAlerts.high_count} high</span>
                )}
              </div>
            )}
            <p className="text-xs text-red-600 mt-1">
              {inventoryAlerts?.total_shortage_value > 0 
                ? `${formatCurrency(inventoryAlerts.total_shortage_value)} shortage value`
                : 'Items below reorder point'
              }
            </p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Active Projects</CardTitle>
            <FolderOpen className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900" data-testid="active-projects">
              {metrics?.active_projects || 0}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {formatCurrency(metrics?.total_project_value || 0)} total value
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Inventory by Type */}
        <Card className="chart-container">
          <CardHeader>
            <CardTitle>Inventory Distribution</CardTitle>
            <CardDescription>Value by item type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={inventoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={120}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {inventoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [formatCurrency(value), name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-1 gap-2 mt-4">
              {inventoryData.map((entry, index) => (
                <div key={entry.name} className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    ></div>
                    <span className="text-gray-600">{entry.name}</span>
                  </div>
                  <span className="font-medium">{entry.percentage}%</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Items by Value */}
        <Card className="chart-container">
          <CardHeader>
            <CardTitle>Top Items by Value</CardTitle>
            <CardDescription>Highest value inventory items</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={metrics?.top_items_by_value || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="item_name" 
                    tick={{ fontSize: 12 }}
                    interval={0}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `$${(value/1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    formatter={(value, name) => [formatCurrency(value), 'Value']}
                    labelFormatter={(label) => `Item: ${label}`}
                  />
                  <Bar dataKey="value" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inventory Alerts Section - Phase 1 INV-003 */}
      {inventoryAlerts && inventoryAlerts.total_alerts > 0 && (
        <Card className="border-l-4 border-l-red-500">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  Inventory Alerts
                </CardTitle>
                <CardDescription>
                  {inventoryAlerts.total_alerts} items require attention • {formatCurrency(inventoryAlerts.total_shortage_value)} shortage value
                </CardDescription>
              </div>
              <div className="flex gap-4 text-sm">
                <div className="text-center">
                  <span className="block text-2xl font-bold text-red-600">{inventoryAlerts.critical_count}</span>
                  <span className="text-xs text-gray-500">Critical</span>
                </div>
                <div className="text-center">
                  <span className="block text-2xl font-bold text-orange-600">{inventoryAlerts.high_count}</span>
                  <span className="text-xs text-gray-500">High</span>
                </div>
                <div className="text-center">
                  <span className="block text-2xl font-bold text-yellow-600">{inventoryAlerts.medium_count}</span>
                  <span className="text-xs text-gray-500">Medium</span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {inventoryAlerts.alerts.slice(0, 6).map((alert) => (
                <div 
                  key={alert.id} 
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    alert.priority === 'critical' ? 'bg-red-50 border-red-200' :
                    alert.priority === 'high' ? 'bg-orange-50 border-orange-200' :
                    alert.priority === 'medium' ? 'bg-yellow-50 border-yellow-200' :
                    'bg-blue-50 border-blue-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      alert.priority === 'critical' ? 'bg-red-500' :
                      alert.priority === 'high' ? 'bg-orange-500' :
                      alert.priority === 'medium' ? 'bg-yellow-500' :
                      'bg-blue-500'
                    }`} />
                    <div>
                      <p className="font-medium text-gray-900">{alert.item_code}</p>
                      <p className="text-sm text-gray-600">{alert.item_name}</p>
                    </div>
                  </div>
                  <div className="text-center px-4">
                    <p className="font-semibold text-gray-900">{alert.current_stock}</p>
                    <p className="text-xs text-gray-500">Current</p>
                  </div>
                  <div className="text-center px-4">
                    <p className="font-semibold text-gray-900">{alert.minimum_stock}</p>
                    <p className="text-xs text-gray-500">Min</p>
                  </div>
                  <div className="flex-1 max-w-xs">
                    <p className="text-sm font-medium text-gray-700">{alert.suggested_action}</p>
                    {alert.suggested_order_qty && (
                      <p className="text-xs text-green-600">
                        Suggested order: {alert.suggested_order_qty} units
                      </p>
                    )}
                  </div>
                  <Badge className={`${
                    alert.alert_type === 'zero_stock' ? 'bg-red-100 text-red-800' :
                    alert.alert_type === 'below_min' ? 'bg-orange-100 text-orange-800' :
                    alert.alert_type === 'below_reorder' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {alert.alert_type.replace('_', ' ').toUpperCase()}
                  </Badge>
                </div>
              ))}
            </div>
            {inventoryAlerts.alerts.length > 6 && (
              <div className="mt-4 text-center">
                <Button variant="outline" size="sm">
                  View All {inventoryAlerts.total_alerts} Alerts
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recent Transactions */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Transactions</CardTitle>
          <CardDescription>Latest inventory movements</CardDescription>
        </CardHeader>
        <CardContent>
          {metrics?.recent_transactions?.length > 0 ? (
            <div className="space-y-4">
              {metrics.recent_transactions.slice(0, 8).map((txn, index) => (
                <div key={txn.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                  <div className="flex items-center space-x-3">
                    <div className={`w-2 h-2 rounded-full ${
                      txn.transaction_type === 'purchase' ? 'bg-green-500' :
                      txn.transaction_type === 'sale' ? 'bg-red-500' :
                      txn.transaction_type === 'production' ? 'bg-blue-500' :
                      'bg-gray-500'
                    }`}></div>
                    <div>
                      <p className="font-medium text-gray-900">{txn.item_name}</p>
                      <p className="text-sm text-gray-500">
                        {txn.transaction_type} • {txn.quantity} units
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">
                      {formatCurrency(txn.total_value)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {new Date(txn.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No recent transactions</p>
              <p className="text-sm">Start by adding inventory transactions</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;